<template>
  <v-container class="agenda-page py-6">
    <v-card class="page-header mb-6" flat dark>
      <v-card-text class="pa-6 pa-md-8">
        <div class="d-flex align-center">
          <v-avatar color="white" size="56" class="mr-4">
            <v-icon color="indigo darken-1" size="30">mdi-calendar-clock</v-icon>
          </v-avatar>
          <div>
            <div class="overline font-weight-bold white--text">Operação do dia a dia</div>
            <h1 class="display-2 font-weight-bold mb-1">Agenda</h1>
            <p class="subtitle-1 mb-0 white--text text--lighten-4">
              Atendimentos marcados, horários travados e confirmações.
            </p>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Barra de ações -->
    <v-card outlined class="pa-4 mb-4">
      <v-row align="center" dense>
        <v-col cols="12" md="4">
          <v-select
            :items="opcoesBarbeiro"
            item-text="nome"
            item-value="id"
            v-model="barbeiroFiltrado"
            label="Barbeiro"
            prepend-inner-icon="mdi-account-filter-outline"
            outlined
            dense
            hide-details
            @change="atualizar"
          ></v-select>
        </v-col>

        <v-col cols="12" md="8" class="d-flex flex-wrap align-center justify-md-end mt-3 mt-md-0">
          <AgendarHorarioDialog class="mr-2 mb-2 mb-md-0" @recarregarLista="atualizar" />
          <TravarHorarioDialog class="mr-2 mb-2 mb-md-0" @recarregarLista="atualizar" />
          <v-btn
            text
            small
            color="grey darken-2"
            :loading="carregando"
            title="Recarregar a agenda"
            @click="atualizar"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Atualizar
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <div class="legenda">
        <span v-for="item in legenda" :key="item.rotulo" class="legenda__item">
          <span class="legenda__cor" :style="{ backgroundColor: item.cor }"></span>
          {{ item.rotulo }}
        </span>
        <span class="legenda__item legenda__item--nota">
          <v-icon x-small color="green darken-2" class="mr-1">mdi-check-circle</v-icon>
          confirmado
        </span>
        <v-spacer></v-spacer>
        <span class="legenda__contagem">
          {{ events.length }} {{ events.length === 1 ? 'agendamento' : 'agendamentos' }} no período
        </span>
      </div>
    </v-card>

    <!-- Calendário -->
    <v-card outlined class="cartao-calendario">
      <div class="barra-calendario">
        <div class="barra-calendario__navegacao">
          <v-btn outlined small class="mr-2" color="grey darken-2" @click="setToday">
            Hoje
          </v-btn>
          <v-btn fab text small color="grey darken-2" @click="prev">
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn fab text small color="grey darken-2" @click="next">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
          <span v-if="$refs.calendar" class="barra-calendario__titulo">
            {{ $refs.calendar.title }}
          </span>
        </div>

        <v-spacer></v-spacer>

        <v-btn-toggle v-model="type" mandatory dense group class="seletor-visao">
          <v-btn v-for="opcao in visoes" :key="opcao.valor" :value="opcao.valor" small text>
            {{ opcao.rotulo }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-progress-linear
        :active="carregando"
        indeterminate
        color="indigo darken-1"
        height="3"
      ></v-progress-linear>

      <v-sheet :height="alturaCalendario">
        <v-calendar
          ref="calendar"
          v-model="focus"
          color="indigo darken-2"
          :events="events"
          :event-color="getEventColor"
          :type="type"
          :categories="categoriasBarbeiros"
          category-show-all
          :first-time="janelaDoDia.primeiro"
          :interval-minutes="30"
          :interval-count="janelaDoDia.quantidade"
          :interval-height="38"
          :short-intervals="false"
          event-overlap-mode="column"
          locale="pt-BR"
          @click:event="showEvent"
          @click:more="viewDay"
          @click:date="viewDay"
          @change="aoMudarIntervalo"
        >
          <!-- Como cada agendamento aparece dentro do calendário -->
          <template v-slot:event="{ event, timed }">
            <div
              class="evento"
              :class="{ 'evento--travado': ehTravado(event) }"
              :style="{ color: corDoTexto(event) }"
            >
              <template v-if="timed">
                <div class="evento__linha1">
                  <v-icon
                    v-if="estaConfirmado(event)"
                    x-small
                    class="evento__check"
                    :color="corDoTexto(event)"
                  >mdi-check-circle</v-icon>
                  <span class="evento__hora">{{ horaDe(event.start) }}</span>
                  <span class="evento__titulo">{{ tituloDoEvento(event) }}</span>
                </div>
                <div class="evento__linha2">
                  {{ descricaoDoEvento(event) }}
                </div>
              </template>
              <template v-else>
                <v-icon
                  v-if="estaConfirmado(event)"
                  x-small
                  class="evento__check"
                  :color="corDoTexto(event)"
                >mdi-check-circle</v-icon>
                <span class="evento__hora">{{ horaDe(event.start) }}</span>
                <span class="evento__titulo">{{ tituloDoEvento(event) }}</span>
              </template>
            </div>
          </template>
        </v-calendar>
      </v-sheet>

      <div v-if="!carregando && !events.length" class="estado-vazio">
        <v-icon color="grey lighten-1" size="42">mdi-calendar-blank-outline</v-icon>
        <div class="estado-vazio__titulo">Nenhum agendamento neste período</div>
        <div class="estado-vazio__texto">
          Use as setas para mudar de período ou o botão "Agendar horário" para criar um.
        </div>
      </div>
    </v-card>

    <!-- Detalhe do agendamento -->
    <v-menu
      v-model="selectedOpen"
      :close-on-content-click="false"
      :activator="selectedElement"
      offset-x
      max-width="380"
    >
      <v-card class="cartao-detalhe" min-width="300" flat>
        <div class="detalhe-topo" :class="ehTravado(selectedEvent) ? 'detalhe-topo--travado' : (selectedEvent.status === 'Confirmado' ? 'detalhe-topo--confirmado' : 'detalhe-topo--pendente')">
          <div class="detalhe-topo__rotulo">
            {{ ehTravado(selectedEvent) ? 'Horário travado' : (selectedEvent.status || 'Pendente') }}
          </div>
          <div class="detalhe-topo__nome">{{ tituloDoEvento(selectedEvent) }}</div>
          <div class="detalhe-topo__horario">
            <v-icon x-small color="white" class="mr-1">mdi-clock-outline</v-icon>
            {{ periodoDoEvento(selectedEvent) }}
          </div>
        </div>

        <v-card-text class="pt-4">
          <div v-if="selectedEvent.servico" class="detalhe-linha">
            <v-icon small color="grey darken-1" class="mr-3">mdi-content-cut</v-icon>
            <div>
              <div class="detalhe-linha__valor">{{ selectedEvent.servico.descricao }}</div>
              <div class="detalhe-linha__nota">
                {{ selectedEvent.servico.tempo }} · R$ {{ selectedEvent.servico.preco }}
              </div>
            </div>
          </div>

          <div v-if="selectedEvent.barbeiro" class="detalhe-linha">
            <v-icon small color="grey darken-1" class="mr-3">mdi-account-tie</v-icon>
            <div>
              <div class="detalhe-linha__valor">{{ selectedEvent.barbeiro.nome }}</div>
              <div class="detalhe-linha__nota">Barbeiro</div>
            </div>
          </div>

          <template v-if="temCliente(selectedEvent)">
            <v-divider class="my-3"></v-divider>

            <div class="detalhe-linha">
              <v-icon small color="grey darken-1" class="mr-3">mdi-account</v-icon>
              <div>
                <div class="detalhe-linha__valor">{{ selectedEvent.cliente.nome }}</div>
                <div class="detalhe-linha__nota">{{ selectedEvent.cliente.email }}</div>
              </div>
            </div>

            <v-btn
              v-if="selectedEvent.cliente.telefone"
              :href="mensagem(selectedEvent)"
              target="_blank"
              color="green darken-2"
              dark
              small
              block
              class="mt-2"
            >
              <v-icon left small>mdi-whatsapp</v-icon>
              {{ selectedEvent.cliente.telefone }}
            </v-btn>
          </template>

          <div class="detalhe-codigo">Código: {{ selectedEvent.id }}</div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="flex-wrap pa-3">
          <v-btn
            v-if="selectedEvent.status !== 'Confirmado'"
            color="success"
            small
            class="mr-2 mb-1"
            @click="confirmarAgendamento"
          >
            <v-icon left small>mdi-check</v-icon>
            Confirmar
          </v-btn>
          <v-btn
            v-else
            color="warning"
            small
            class="mr-2 mb-1"
            @click="desconfirmarAgendamento"
          >
            <v-icon left small>mdi-undo</v-icon>
            Não confirmar
          </v-btn>

          <AgendaAjuste
            class="mb-1"
            :itemPassado="selectedEvent"
            @attAgenda="atualizar"
          ></AgendaAjuste>

          <v-spacer></v-spacer>

          <v-btn text small color="grey darken-1" class="mb-1" @click="selectedOpen = false">
            Fechar
          </v-btn>
        </v-card-actions>

        <v-divider></v-divider>

        <!-- Excluir fica isolado no rodapé, longe do "Fechar": encostado nele
             era fácil errar o alvo, ainda mais no celular. -->
        <div class="rodape-perigo">
          <v-btn text x-small color="red darken-2" @click="apagarAgendamento">
            <v-icon left x-small>mdi-delete-outline</v-icon>
            Excluir agendamento
          </v-btn>
        </div>
      </v-card>
    </v-menu>
  </v-container>
</template>

<script>
//import firebase from 'firebase'
import { mapGetters } from "vuex";

import { refAgenda, refBarbeiros } from "@/firebaseConfig";
import { excluirAgendamentoNoPortal } from "@/services/agendaFirestore";
// Um tom de azul por barbeiro. Passos da rampa azul escolhidos com a maior
// separação de luminância possível nos primeiros — que é o caso real, com
// poucos barbeiros. A cor do texto de cada tom foi conferida por contraste.
const TONS_BARBEIRO = [
  { fundo: "#0d366b", texto: "#ffffff" },
  { fundo: "#3987e5", texto: "#0b0b0b" },
  { fundo: "#86b6ef", texto: "#0b0b0b" },
  { fundo: "#1c5cab", texto: "#ffffff" },
  { fundo: "#6da7ec", texto: "#0b0b0b" },
  { fundo: "#104281", texto: "#ffffff" },
  { fundo: "#256abf", texto: "#ffffff" },
  { fundo: "#9ec5f4", texto: "#0b0b0b" },
];

const COR_TRAVADO = { fundo: "#546e7a", texto: "#ffffff" };

import TravarHorarioDialog from "./Components/TravarHorarioDialog";
import AgendarHorarioDialog from "./Components/AgendarHorarioDialog";
import AgendaAjuste from "./Components/AgendaAjuste";

export default {
  components: {
    TravarHorarioDialog,
    AgendaAjuste,
    AgendarHorarioDialog,
  },
  data() {
    return {
      refAgenda: refAgenda,
      refBarbeiros: refBarbeiros,
      barbeiroFiltrado: "",
      barbeiros: [],
      events: [],
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      focus: "",
      type: "month",
      carregando: false,
      // Faixa de datas que o calendário está exibindo. É ela que define o que
      // buscar — antes a consulta era fixa em 15 dias e os meses anteriores
      // apareciam vazios mesmo tendo agendamentos.
      intervalo: { inicio: null, fim: null },
      visoes: [
        { valor: "category", rotulo: "Barbeiros" },
        { valor: "day", rotulo: "Dia" },
        { valor: "4day", rotulo: "4 dias" },
        { valor: "week", rotulo: "Semana" },
        { valor: "month", rotulo: "Mês" },
      ],
    };
  },

  mounted() {
    //this.$refs.calendar.scrollToTime('08:00');
    this.carregarBarbeiros();
    // Dispara o @change do calendário, que define o intervalo e carrega.
    this.$refs.calendar.checkChange();
  },
  computed: {
    ...mapGetters(["getCurrentUser", "isUserAuth"]),

    opcoesBarbeiro() {
      return [{ id: "", nome: "Todos os barbeiros" }, ...this.barbeiros];
    },

    podeVerTodos() {
      return (
        this.$store.getters.getPermissoes[this.getCurrentUser.lvl] >= 3
      );
    },

    alturaCalendario() {
      return this.$vuetify.breakpoint.smAndDown ? 520 : 700;
    },

    // Cor fixa por barbeiro. A ordenação por id mantém o tom estável entre
    // carregamentos — o Firestore não garante a ordem dos documentos.
    coresPorBarbeiro() {
      const mapa = {};
      [...this.barbeiros]
        .sort((a, b) => (String(a.id) > String(b.id) ? 1 : -1))
        .forEach((barbeiro, indice) => {
          mapa[barbeiro.id] = TONS_BARBEIRO[indice % TONS_BARBEIRO.length];
        });
      return mapa;
    },

    // Colunas da visão "Barbeiros". Com category-show-all, cada uma aparece
    // mesmo sem nenhum horário preenchido.
    categoriasBarbeiros() {
      const lista = this.barbeiroFiltrado
        ? this.barbeiros.filter((item) => item.id === this.barbeiroFiltrado)
        : this.barbeiros;
      return lista.map((item) => item.nome).filter(Boolean);
    },

    legenda() {
      const lista = (this.barbeiroFiltrado
        ? this.barbeiros.filter((item) => item.id === this.barbeiroFiltrado)
        : this.barbeiros
      ).map((barbeiro) => ({
        rotulo: barbeiro.nome,
        cor: (this.coresPorBarbeiro[barbeiro.id] || TONS_BARBEIRO[0]).fundo,
      }));

      lista.push({ rotulo: "Horário travado", cor: COR_TRAVADO.fundo });
      return lista;
    },

    // A visão de dia mostrava as 24 horas. Aqui a faixa se ajusta ao que
    // realmente existe na agenda, com folga de uma hora de cada lado.
    janelaDoDia() {
      const padrao = { primeiro: "08:00", quantidade: 26 }; // 08:00 às 21:00

      const horas = this.events
        .map((evento) => ({
          inicio: this.horaCheia(evento.start),
          fim: this.horaCheia(evento.end),
        }))
        // Number("") devolve 0: sem esta checagem uma data malformada
        // encolheria a visão do dia para a faixa 00:00–01:00.
        .filter((item) => item.inicio !== null && item.fim !== null);

      if (!horas.length) {
        return padrao;
      }

      const primeiraHora = Math.max(0, Math.min(...horas.map((h) => h.inicio)) - 1);
      const ultimaHora = Math.min(24, Math.max(...horas.map((h) => h.fim)) + 1);

      if (ultimaHora <= primeiraHora) {
        return padrao;
      }

      return {
        primeiro: `${String(primeiraHora).padStart(2, "0")}:00`,
        quantidade: (ultimaHora - primeiraHora) * 2,
      };
    },
  },
  methods: {
    mensagem(item) {
      // item.start vem como "YYYY-MM-DD HH:mm"
      const [dataParte, horaParte] = String(item.start).split(" ");
      const [, mes, dia] = dataParte.split("-");
      const hora = horaParte || item.start.substring(item.start.length - 5);

      // Mantém só os dígitos: a máscara é (##)#####-####, mas números
      // salvos em outros formatos também precisam funcionar.
      const telefone = String(item.cliente.telefone).replace(/\D/g, "");
      const servico = item.servico.descricao;

      const texto =
        `Olá, gostaria de confirmar seu agendamento para às ${hora}, ` +
        `no dia ${dia}/${mes} na Barbearia Lisboa, para o serviço de ${servico}`;

      return `https://api.whatsapp.com/send?phone=55${telefone}&text=${encodeURIComponent(
        texto
      )}&app_absent=0`;
    },
    // O calendário avisa qual faixa está na tela sempre que muda de mês,
    // semana ou dia. É o gatilho para buscar o período certo.
    aoMudarIntervalo({ start, end }) {
      if (!start || !end) {
        return;
      }

      // O fuso -03:00 é o mesmo usado ao gravar o dataQuery no agendamento.
      this.intervalo = {
        inicio: new Date(`${start.date}T00:00:00-03:00`).getTime(),
        fim: new Date(`${end.date}T23:59:59-03:00`).getTime(),
      };

      this.carregarAgenda();
    },

    async carregarAgenda() {
      if (!this.intervalo.inicio || !this.intervalo.fim) {
        return;
      }

      this.carregando = true;
      try {
        let consulta = this.refAgenda
          .where("dataQuery", ">=", this.intervalo.inicio)
          .where("dataQuery", "<=", this.intervalo.fim);

        if (this.podeVerTodos) {
          // Nível 3 ou mais vê tudo; o filtro de barbeiro é opcional.
          if (this.barbeiroFiltrado) {
            consulta = consulta.where("barbeiro.id", "==", this.barbeiroFiltrado);
          }
        } else {
          // Nível menor enxerga apenas a própria agenda.
          consulta = consulta.where(
            "barbeiro.email",
            "==",
            this.getCurrentUser.email
          );
        }

        const snap = await consulta.get();
        const eventos = [];
        snap.forEach((doc) => {
          const agendamento = this.tratarAgendamento(doc);
          if (agendamento) {
            eventos.push(agendamento);
          }
        });

        this.events = eventos;
      } catch (error) {
        this.$toasted.global.defaultError({
          msg: "Não foi possível carregar a agenda: " + error.message,
        });
      } finally {
        this.carregando = false;
      }
    },
    // Devolve o evento pronto ou null quando o registro está fora do padrão.
    tratarAgendamento(doc) {
      const agendamento = doc.data();

      if (
        !agendamento.start ||
        !agendamento.end ||
        agendamento.start.length != 16 ||
        agendamento.end.length != 16
      ) {
        return null;
      }

      agendamento.start = this.corrigirData(agendamento.start);
      agendamento.end = this.corrigirData(agendamento.end);

      // A visão "Barbeiros" agrupa por esta propriedade.
      agendamento.category = agendamento.barbeiro
        ? agendamento.barbeiro.nome
        : "Sem barbeiro";

      return { ...agendamento };
    },

    // A cor passou a identificar o BARBEIRO. O status vira ícone dentro do
    // evento, para não perder essa informação.
    tomDoEvento(evento) {
      if (this.ehTravado(evento)) {
        return COR_TRAVADO;
      }
      const id = evento && evento.barbeiro ? evento.barbeiro.id : null;
      return this.coresPorBarbeiro[id] || TONS_BARBEIRO[0];
    },

    getEventColor(event) {
      return this.tomDoEvento(event).fundo;
    },

    corDoTexto(evento) {
      return this.tomDoEvento(evento).texto;
    },

    estaConfirmado(evento) {
      return !!(
        evento &&
        evento.status &&
        String(evento.status).toLowerCase() === "confirmado"
      );
    },

    // Horário travado é criado pelo TravarHorarioDialog com um cliente
    // fictício; visualmente não pode se confundir com atendimento real.
    ehTravado(evento) {
      if (!evento || !evento.cliente) {
        return false;
      }
      return (
        evento.cliente.email === "Travado" ||
        String(evento.name || "").includes("HORÁRIO TRAVADO")
      );
    },

    temCliente(evento) {
      return !!(evento && evento.cliente && !this.ehTravado(evento));
    },

    horaDe(valor) {
      return String(valor || "").substring(11, 16);
    },

    // Hora cheia de "YYYY-MM-DD HH:mm", ou null se não for uma data válida.
    horaCheia(valor) {
      const texto = String(valor || "");
      if (texto.length < 16) {
        return null;
      }
      const hora = Number(texto.substring(11, 13));
      return Number.isInteger(hora) && hora >= 0 && hora <= 23 ? hora : null;
    },

    periodoDoEvento(evento) {
      if (!evento || !evento.start) {
        return "";
      }
      const [data] = String(evento.start).split(" ");
      const [ano, mes, dia] = String(data).split("-");
      return `${dia}/${mes}/${ano} · ${this.horaDe(evento.start)} às ${this.horaDe(evento.end)}`;
    },

    tituloDoEvento(evento) {
      if (!evento) {
        return "";
      }
      if (this.ehTravado(evento)) {
        return "Horário travado";
      }
      // O nome do cliente pode vir em "nome" ou "name" conforme a origem.
      const cliente = evento.cliente || {};
      return cliente.nome || cliente.name || evento.name || "Sem cliente";
    },

    descricaoDoEvento(evento) {
      if (!evento) {
        return "";
      }
      const servico = evento.servico ? evento.servico.descricao : "";
      const barbeiro = evento.barbeiro ? evento.barbeiro.nome : "";

      if (this.ehTravado(evento)) {
        return barbeiro;
      }
      // Sem filtro de barbeiro, saber de quem é o horário importa.
      return [servico, barbeiro].filter(Boolean).join(" · ");
    },
    corrigirData(data) {
      if (data.includes("T")) {
        return data.replace("T", " ");
      } else {
        return data;
      }
    },

    carregarBarbeiros() {
      this.refBarbeiros
        .get()
        .then((snap) => {
          const lista = [];
          snap.forEach((doc) => {
            lista.push({ ...doc.data() });
          });
          this.barbeiros = lista;
        })
        .catch((error) => {
          this.$toasted.global.defaultError({
            msg: "Não foi possível carregar os barbeiros: " + error.message,
          });
        });
    },

    async apagarAgendamento() {
      //this.selectedEvent.id
      if (confirm("Deseja cancelar este agendamento?")) {
        try {
          await excluirAgendamentoNoPortal(this.selectedEvent);
          this.atualizar();
          this.selectedOpen = false;
        } catch (error) {
          this.$toasted.global.defaultError({ msg: error.message });
        }
      }
    },

    confirmarAgendamento() {
      this.alterarStatus("Confirmado");
    },

    desconfirmarAgendamento() {
      this.alterarStatus("Pendente");
    },

    alterarStatus(status) {
      this.refAgenda
        .doc(this.selectedEvent.id)
        .update({ status: status })
        .then(() => {
          this.$toasted.global.defaultSuccess(
            status === "Confirmado"
              ? "Agendamento confirmado!"
              : "Confirmação removida."
          );
          this.atualizar();
        })
        .catch((error) => {
          // Antes isso ia só para o console e a tela não dizia nada.
          this.$toasted.global.defaultError({
            msg: "Não foi possível alterar o status: " + error.message,
          });
        });
    },

    atualizar() {
      this.selectedOpen = false;
      this.carregarAgenda();
    },
    viewDay({ date }) {
      this.focus = date;
      this.type = "day";
    },

    setToday() {
      this.focus = "";
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        this.selectedElement = nativeEvent.target;
        setTimeout(() => (this.selectedOpen = true), 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
  },
};
</script>

<style lang="scss" scoped>
.agenda-page {
  max-width: 1280px;
}

.page-header {
  background: linear-gradient(135deg, #283593 0%, #5c6bc0 100%) !important;
  border-radius: 24px 24px 24px 6px;
}

/* ---------- legenda ---------- */

.legenda {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #5f6b7a;
}

.legenda__item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legenda__cor {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  display: inline-block;
}

.legenda__item--nota {
  color: #78909c;
}

.legenda__contagem {
  font-weight: 700;
  color: #37474f;
}

/* ---------- barra do calendário ---------- */

.cartao-calendario {
  border-radius: 18px;
  overflow: hidden;
}

.barra-calendario {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  background: #ffffff;
  border-bottom: 1px solid #eceff1;
}

.barra-calendario__navegacao {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.barra-calendario__titulo {
  font-size: 17px;
  font-weight: 700;
  color: #263238;
  margin-left: 10px;
  text-transform: capitalize;
}

.seletor-visao {
  flex-wrap: wrap;
}

/* ---------- evento dentro do calendário ---------- */

.evento {
  padding: 2px 6px;
  overflow: hidden;
  height: 100%;
  line-height: 1.25;
}

.evento__linha1 {
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.evento__hora {
  font-size: 11px;
  font-weight: 700;
  opacity: 0.9;
  font-variant-numeric: tabular-nums;
}

/* O status virou ícone: a cor do bloco agora identifica o barbeiro */
.evento__check {
  margin-right: 3px;
}

.evento__titulo {
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.evento__linha2 {
  font-size: 11px;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Listra diagonal deixa o horário travado distinto à distância */
.evento--travado {
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.16) 0 6px,
    transparent 6px 12px
  );
}

/* ---------- estado vazio ---------- */

.estado-vazio {
  text-align: center;
  padding: 26px 16px 30px;
  border-top: 1px solid #eceff1;
}

.estado-vazio__titulo {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}

.estado-vazio__texto {
  font-size: 13px;
  color: #78909c;
  margin-top: 2px;
}

/* ---------- detalhe do agendamento ---------- */

.cartao-detalhe {
  border-radius: 16px;
  overflow: hidden;
}

.detalhe-topo {
  padding: 14px 16px;
  color: #ffffff;
}

.detalhe-topo--confirmado { background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%); }
.detalhe-topo--pendente   { background: linear-gradient(135deg, #283593 0%, #5c6bc0 100%); }
.detalhe-topo--travado    { background: linear-gradient(135deg, #37474f 0%, #607d8b 100%); }

.detalhe-topo__rotulo {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.9;
}

.detalhe-topo__nome {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  margin: 2px 0 4px;
  overflow-wrap: anywhere;
}

.detalhe-topo__horario {
  font-size: 12px;
  opacity: 0.95;
  display: flex;
  align-items: center;
}

.detalhe-linha {
  display: flex;
  align-items: flex-start;
}

.detalhe-linha + .detalhe-linha {
  margin-top: 12px;
}

.detalhe-linha__valor {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  overflow-wrap: anywhere;
}

.detalhe-linha__nota {
  font-size: 12px;
  color: #78909c;
  overflow-wrap: anywhere;
}

.detalhe-codigo {
  margin-top: 14px;
  font-size: 10.5px;
  color: #b0bec5;
}

/* Faixa própria para a ação destrutiva, alinhada à direita */
.rodape-perigo {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px;
  background: #fafafa;
}

.v-calendar-weekly__day-more {
  color: #1976d2 !important;
  font-weight: bold;
}

@media (max-width: 600px) {
  .page-header {
    border-radius: 18px 18px 18px 4px;
  }

  .page-header .display-2 {
    font-size: 2.1rem !important;
  }

  .barra-calendario {
    padding: 10px 12px;
  }

  .barra-calendario__titulo {
    font-size: 15px;
    width: 100%;
    margin: 6px 0 0;
  }
}
</style>
